/**
 * What a phone calls this when somebody puts it on their home screen.
 *
 * The same defect as the mail, in a place nobody thought to look. Every host served one
 * manifest and one set of icons, both WVCEA's, because they were files in a public folder
 * rather than anything that knew whose request it was answering. So adding the Verity
 * Connect operations page to a phone offered to install it as "WVCEA", with WVCEA's logo,
 * on a domain WVCEA does not own.
 *
 * A home-screen icon is also the one thing here that cannot be corrected afterwards: the
 * phone copies the picture and the name at the moment somebody adds it and never asks
 * again. Getting it wrong is not a bug somebody reports, it is a wrong icon sitting on a
 * teacher's phone until she deletes it.
 */

export interface InstallIdentity {
  /** The full name. The manifest's `name`, and the page title. */
  name: string;
  /** What a home screen has room for under the icon. The manifest's `short_name`. */
  shortName: string;
  description: string;
  /** The colour a phone paints around the app. */
  themeColor: string;
  /** Where the icon files live, without a trailing slash. */
  iconBase: string;
}

/**
 * What VerityWord's own surfaces install as, and what an association that has not been
 * given its own artwork gets.
 *
 * Deliberately not WVCEA — the same rule as the mail. An association with no icon of its
 * own should get a neutral one, never the first customer's logo, because a customer whose
 * app carries somebody else's mark has been told something untrue about who they are
 * dealing with.
 */
export const PLATFORM_INSTALL_IDENTITY: InstallIdentity = {
  name: "Verity Connect",
  shortName: "Verity",
  description: "Running the conventions and associations that use Verity Connect.",
  themeColor: "#2B3A55",
  iconBase: "/brand/verity",
};

export interface AssociationInstallFields {
  appName: string | null;
  name: string | null;
  shortName: string | null;
  primaryColor: string | null;
  /** Null until an association has artwork of its own. */
  iconBase: string | null;
}

/** Field by field, so an association that is named but has no artwork is still named. */
export function associationInstallIdentity(
  fields: AssociationInstallFields,
  fallback: InstallIdentity = PLATFORM_INSTALL_IDENTITY,
): InstallIdentity {
  const name = text(fields.appName) ?? fallback.name;
  const organization = text(fields.name);
  return {
    name,
    shortName: text(fields.shortName) ?? name,
    description: organization
      ? `The convention guide, schedule, and records for ${organization}.`
      : fallback.description,
    themeColor: color(fields.primaryColor) ?? fallback.themeColor,
    iconBase: iconPath(fields.iconBase) ?? fallback.iconBase,
  };
}

export interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: "any" | "maskable";
}

export interface WebManifest {
  name: string;
  short_name: string;
  description: string;
  start_url: string;
  display: "standalone";
  background_color: string;
  theme_color: string;
  icons: ManifestIcon[];
}

export function webManifest(identity: InstallIdentity): WebManifest {
  return {
    name: identity.name,
    short_name: identity.shortName,
    description: identity.description,
    start_url: "/",
    display: "standalone",
    background_color: "#ffffff",
    theme_color: identity.themeColor,
    icons: [
      { src: `${identity.iconBase}/icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: `${identity.iconBase}/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "any" },
      // Android crops this one into a circle, so it is drawn with room to lose.
      { src: `${identity.iconBase}/icon-maskable-512.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
  };
}

/**
 * A colour a phone will accept. Anything else falls back rather than being passed through,
 * because a malformed theme colour is ignored silently and looks like the setting not working.
 */
function color(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed && /^#[0-9a-f]{6}$/iu.test(trimmed) ? trimmed.toUpperCase() : null;
}

/**
 * A path under this origin, and nothing else. An icon base is written by an association,
 * and one pointing at another site would put somebody else's picture on a phone.
 */
function iconPath(value: string | null): string | null {
  const trimmed = value?.trim().replace(/\/+$/u, "");
  if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  return /^[/A-Za-z0-9_-]+$/u.test(trimmed) ? trimmed : null;
}

function text(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * How the installed application names itself, appended to its User-Agent.
 *
 * It lives here, in the one package both the Worker and the web application read, because
 * the same fact is now needed in two places: the Worker marks the document it serves, and
 * the document marks itself when the copy it came from has no mark. Two literals would
 * agree until one of them was edited.
 *
 * The shells append `VerityConnectShell/1`, so this is matched as a substring rather than
 * compared: the version after the slash is theirs to move.
 */
export const SHELL_USER_AGENT_TOKEN = "VerityConnectShell";

/** Which installed application is asking, or null for an ordinary browser. */
export type ShellPlatform = "ios" | "android";

/**
 * Which of the two shells this User-Agent belongs to.
 *
 * Both shells append the same token, so the platform comes from what the system put in
 * front of it: `Android` on one, `iPhone` or `iPad` on the other. An unrecognised shell is
 * called iOS, which is what it was called when the attribute was a constant.
 */
export function shellPlatformForUserAgent(userAgent: string): ShellPlatform | null {
  if (!userAgent.includes(SHELL_USER_AGENT_TOKEN)) return null;
  return /\bAndroid\b/u.test(userAgent) ? "android" : "ios";
}

/**
 * Which generation of the installed application this is.
 *
 * The number after the slash. It exists because the server had no way to tell one build of
 * a shell from another, and the one question worth asking of a phone during a convention is
 * whether it is running the version with the fix in it. Nineteen iPhones held no push token
 * for a fortnight and every one of them looked, from here, exactly like a phone that worked.
 *
 * It is a generation and not a build number on purpose. A build changes whenever anything
 * is compiled; this moves only when the server has to behave differently towards the two,
 * so a shell that gains a colour does not make the server think anybody is behind.
 *
 * An unnumbered or unreadable token is the first generation, because that is what every
 * shell in somebody's hand was before anybody numbered them.
 */
export function shellGenerationForUserAgent(userAgent: string): number | null {
  if (!userAgent.includes(SHELL_USER_AGENT_TOKEN)) return null;
  const found = new RegExp(`${SHELL_USER_AGENT_TOKEN}/(\\d{1,4})`, "u").exec(userAgent);
  const generation = found ? Number(found[1]) : Number.NaN;
  return Number.isFinite(generation) && generation >= 1 ? generation : 1;
}

/**
 * Whether this is a phone browser rather than the installed application or a desktop.
 *
 * Thirty-four teachers signed in on a phone and never installed anything, and the phone
 * website is the one place they can be reached. They are offered the two stores instead of
 * the guide, so that the notifications, the recording and the lock screen card — none of
 * which a browser can do — stop being invisible to exactly the people who need them.
 *
 * Never the installed application: its web views carry the shell's name and must keep being
 * served the real thing. Never a desktop either — a store badge is no use on a laptop, and
 * WVCEA's own staff work on one.
 */
export function phoneBrowserForUserAgent(userAgent: string): ShellPlatform | null {
  if (userAgent.includes(SHELL_USER_AGENT_TOKEN)) return null;
  if (/\bAndroid\b/u.test(userAgent) && /\bMobile\b/u.test(userAgent)) return "android";
  // iPadOS reports itself as a Macintosh, and an iPad is not the phone this is for.
  if (/\b(iPhone|iPod)\b/u.test(userAgent)) return "ios";
  return null;
}

/**
 * The newest generation of each shell that a person can actually install.
 *
 * Raised when a store has the new one, not when it is built: the whole purpose of this is
 * telling somebody to update, and telling a teacher to fetch a version Apple has not
 * approved yet is worse than saying nothing. The switch on the operations page is the
 * second half of that — this says a newer one exists, the switch says it is out.
 */
export const NEWEST_SHELL_GENERATION: Readonly<Record<ShellPlatform, number>> = {
  /*
   * Third, and downloadable: 1.0.8 was approved on the evening of 17 September and the
   * store began serving it about fifteen minutes later, which was checked by asking the
   * store rather than by trusting the approval mail. It carries the upload that survives a
   * phone going into a pocket, audio compressed before it is sent, and the code a camera
   * reads off the square on a door — which the shell before it threw away, so every scan at
   * the convention asked a teacher to type it in by hand.
   */
  ios: 3,
  /*
   * Third, and on the store: version code 13 went to Google on the same evening and was at
   * full rollout within minutes. It carries the same check-in fix, a recording that waits
   * on the phone until WVCEA has it instead of being thrown away, one file per recording
   * rather than one per session, and audio compressed before it is sent.
   *
   * Raised only after that build was actually downloadable. A banner a teacher cannot
   * satisfy never goes away.
   */
  android: 3,
};

/**
 * Whether this phone is running a shell older than the one in the store.
 *
 * Deliberately not "older than the newest build we made". Both halves have to be true: a
 * newer generation has to exist for this platform, and somebody has to have said it is
 * available. Either alone puts a banner on a phone that can do nothing about it.
 */
export function shellIsBehind(
  platform: ShellPlatform | null,
  generation: number | null,
  updateIsOut: boolean,
): boolean {
  if (platform === null || generation === null || !updateIsOut) return false;
  return generation < NEWEST_SHELL_GENERATION[platform];
}

/**
 * What the Worker writes down about a shell, so a request that arrives without the token
 * is still recognised as the installed application.
 *
 * Android gives the installed application no way to name itself on every request. The
 * shell appends its token to the WebView's User-Agent, and that covers the WebView — but
 * a service worker's requests go out on a separate network stack with its own User-Agent,
 * and `ServiceWorkerWebSettings` has no way to set it. So the worker that serves every
 * page of the installed application asks for those pages as an anonymous Android browser,
 * and was answered with the two app stores instead of the convention.
 *
 * Measured on a device rather than reasoned about: inside the application the page reports
 * `…Mobile Safari/537.36 VerityConnectShell/2` and its own service worker reports
 * `…Mobile Safari/537.36`, and the document the worker had saved under "/" carried
 * `data-phone-browser="android"`.
 *
 * A cookie is what both halves share. The two shells keep the ordinary WebView cookie
 * store — the native compatibility contract requires it — and a service worker's requests
 * carry that store's cookies even though they cannot carry its User-Agent. That was
 * measured too, on the same device, in the same minute.
 *
 * The generation travels with the platform because the update notice needs it, and a
 * request that lost the token lost the version number written beside it.
 */
export const SHELL_MEMORY_COOKIE = {
  secure: "__Host-verity_shell",
  local: "verity_shell_local",
} as const;

/** How long the Worker keeps remembering. The longest a browser will hold a cookie. */
export const SHELL_MEMORY_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

export interface RememberedShell {
  platform: ShellPlatform;
  generation: number;
}

/** What is written in the cookie: the platform and the generation, nothing else. */
export function rememberedShellValue(platform: ShellPlatform, generation: number): string {
  return `${platform}/${generation}`;
}

/**
 * What the cookie says, or null if it says nothing this function recognises.
 *
 * Strict on purpose. This is read from something a browser sends, and the answer decides
 * whether somebody is shown the convention or an advertisement for it. Anything that is
 * not exactly one of the two platform names and a small whole number is no answer at all.
 */
export function rememberedShell(value: string | null | undefined): RememberedShell | null {
  const found = /^(ios|android)\/(\d{1,4})$/u.exec((value ?? "").trim());
  if (!found) return null;
  const generation = Number(found[2]);
  if (!Number.isFinite(generation) || generation < 1) return null;
  return { platform: found[1] as ShellPlatform, generation };
}
